'use client';
import '../theme/fonts.css';
import '../theme/global.css';
import type { AppProps } from 'next/app';
import { GlobalStyles, GlobalStylesProps } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { initializeMetaMaskSDK } from '@/configuration/metaMaskSdk';
import { ContentObject } from '@/interface';
import AppLayout from '@/layouts/AppLayout';
import { ClaimManagementProvider } from '@/lib';
import { NftContentProvider } from '@/lib/providers/NftContextProvider';
import { ClaimModalOpenProvider } from '@/provider/ClaimModalOpenProvider';
import { ContentProvider } from '@/provider/ContentProvider';
import { MenuProvider } from '@/provider/MenuContext';
import { RuntimeConfiguration } from '../configuration';
import { makeTheme, createStyles } from '../theme';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import GoogleAnalytics from '@/Component/GoogleAnalytics';
import { ChainName } from '@/lib/interface';
import { JSON_CONTENT } from '@/assets/json';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false
    }
  }
});

export const initialMetaMaskSdk = initializeMetaMaskSDK();

export default function App({ Component, pageProps }: AppProps) {
  const [content, setContent] = useState<ContentObject>({} as ContentObject);
  const hasFetched = useRef<boolean>(false);

  const theme = useMemo(() => {
    if (content?.theme) return makeTheme(content?.theme);
  }, [content]);

  const styles = useMemo(() => {
    if (!content.theme) {
      return {};
    }
    return createStyles(content.theme) as GlobalStylesProps['styles'];
  }, [content]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    (async () => {
      try {
        const res = await fetch(JSON_CONTENT.CONTENT, { cache: 'no-store' });
        if (!res.ok) {
          throw new Error(`Failed to load content.json: ${res.status} ${res.statusText}`);
        }
        const contentData = (await res.json()) as unknown;
        setContent(contentData as ContentObject);
      } catch (e) {
        console.debug('e: ', e);
      }
    })();
  }, []);

  return (
    <>
      {theme && content && (
        <QueryClientProvider client={queryClient}>
          <ContentProvider content={content}>
            <NftContentProvider>
              <MenuProvider>
                <ClaimModalOpenProvider>
                  <ClaimManagementProvider
                    theme={theme}
                    activeChain={(RuntimeConfiguration.ACTIVE_CHAIN_ID ?? 'sepolia') as ChainName}
                    clientId={RuntimeConfiguration.CLIENT_ID}
                    walletOption={content?.walletOptions}
                    onAuthenticated={() => {}}
                  >
                    <GlobalStyles styles={styles} />
                    <AppLayout>
                      <GoogleAnalytics />
                      <Component {...pageProps} />
                    </AppLayout>
                  </ClaimManagementProvider>
                </ClaimModalOpenProvider>
              </MenuProvider>
            </NftContentProvider>
          </ContentProvider>
          <ReactQueryDevtools position="left" initialIsOpen={false} />
        </QueryClientProvider>
      )}
    </>
  );
}
